/** 页面切换只分离视图；请求和消息对象继续存活到该轮生成结束。 */
type Store = { getState: () => any; dispatch: (action: any) => any; subscribe?: (listener: () => void) => () => void };
const sessions = new Map<string, ChatRequestRuntime>();

export class ChatRequestRuntime {
  private attached = true;
  private unsubscribe?: () => void;
  private disposed = false;
  private snapshot: any;
  private parameters: any;
  private token: string;
  readonly controller: AbortController;

  constructor(readonly sessionId: string, private live: Store, controller: AbortController) {
    const root = live.getState();
    sessions.get(sessionId)?.dispose();
    this.token = root.userInfo?.lbChatUserInfo?.loginToken || "";
    this.controller = controller;
    this.snapshot = { ...root.chatMessage, chatGptConfig: { ...root.chatMessage.chatGptConfig },
      chatMessageList: [...root.chatMessage.chatMessageList], cancelToken: controller,
      chatPending: true, chatRequestPreparing: true };
    this.parameters = { ...root.chatParameter };
    sessions.set(sessionId, this);
    live.dispatch({ type: "chatMessage/setData", payload: this.snapshot });
    // 退出登录或更换账号时关闭旧账号的连接并清除内存正文。
    this.unsubscribe = live.subscribe?.(() => {
      if ((live.getState().userInfo?.lbChatUserInfo?.loginToken || "") !== this.token) this.dispose();
    });
  }

  private isAttached() {
    return !this.disposed && this.attached && this.live.getState().chatMessage.cancelToken === this.controller;
  }

  get state() {
    if (this.isAttached()) {
      const current = this.live.getState().chatMessage;
      this.snapshot.chatPending = current.chatPending;
      this.snapshot.chatRequestPreparing = current.chatRequestPreparing;
    }
    return this.snapshot;
  }

  getState = () => ({ ...this.live.getState(), chatParameter: this.parameters,
    chatMessage: this.disposed ? { ...this.snapshot, cancelToken: null } : this.state });

  dispatch = (action: any): any => {
    if (this.disposed) return;
    if (!action.type.includes("/")) action = { ...action, type: `chatMessage/${action.type}` };
    if (action.type === "chatMessage/setData") {
      this.snapshot = { ...this.state, ...action.payload };
      if (this.isAttached()) this.live.dispatch(action);
      this.pruneCompleted();
      return;
    }
    if (action.type === "chatMessage/stop" && action.payload?.technicalFailure) {
      this.controller.abort();
      this.dispatch({ type: "chatMessage/setData", payload: { chatPending: false, chatRequestPreparing: false } });
      return;
    }
    if (action.type.startsWith("chatParameter/")) {
      // 后台回调不得清空新会话的输入、附件或修改当前输入框状态。
      if (this.isAttached()) return this.live.dispatch(action);
      return;
    }
    if (["chatMessage/syncFinalMessageTime", "chatMessage/getUserFreeTimes",
      "leftSideX/refreshConversationCount"].includes(action.type) || this.isAttached()) {
      return this.live.dispatch(action);
    }
  };

  detach() {
    if (!this.isAttached()) return false;
    this.state; // 捕获显式停止等外部操作更新后的 loading 状态。
    this.attached = false;
    this.live.dispatch({ type: "chatMessage/setData", payload: {
      chatPending: false, chatRequestPreparing: false, cancelToken: new AbortController(),
    } });
    return true;
  }

  restore() {
    if (this.disposed) return false;
    this.attached = true;
    this.live.dispatch({ type: "chatMessage/setData", payload: this.snapshot });
    return true;
  }

  /** 服务端已持久化终态后释放缓存；更早返回的占位历史不能覆盖本地正文。 */
  reconcile(history: any[]) {
    const answer = this.state.chatMessageList.findLast((item: any) => item.role === "assistant");
    const saved = history.find(item => (item.requestId || item.agentRequestId || item.f_agent_request_id) &&
      (item.requestId || item.agentRequestId || item.f_agent_request_id) === answer?.requestId);
    if (saved && !this.state.chatPending && !this.state.chatRequestPreparing &&
        (["COMPLETED", "FAILED", "CANCELLED", "PARTIAL", "OUTCOME_UNKNOWN"].includes(
          saved.generationStatus || saved.agentStatus || saved.f_agent_status) ||
        (saved.id && answer?.content && saved.messageContent === answer.content))) {
      sessions.delete(this.sessionId);
      this.unsubscribe?.();
      return true;
    }
    return false;
  }

  private pruneCompleted() {
    // 只淘汰已结束的后台缓存，不中止仍在生成的请求。
    const completed = [...sessions.values()].filter(item => !item.isAttached() &&
      !item.snapshot.chatPending && !item.snapshot.chatRequestPreparing);
    completed.slice(0, Math.max(0, completed.length - 20)).forEach(item => item.dispose());
  }

  dispose() {
    if (this.disposed) return;
    const wasAttached = this.isAttached();
    this.disposed = true;
    this.unsubscribe?.();
    if (sessions.get(this.sessionId) === this) sessions.delete(this.sessionId);
    this.snapshot.chatMessageList.forEach((item: any) => { item.isDone = true; });
    this.controller.abort();
    this.snapshot.chatMessageList = [];
    this.parameters = {};
    if (wasAttached) this.live.dispatch({ type: "chatMessage/setData", payload: {
      chatMessageList: [], chatPending: false, chatRequestPreparing: false,
      cancelToken: new AbortController(),
    } });
  }
}

export function findChatRuntime(sessionId: unknown) {
  return sessions.get(String(sessionId));
}

export function detachChatRequest(controller: AbortController, sessionId?: unknown) {
  const runtime = [...sessions.values()].find(item => item.controller === controller &&
    (sessionId === undefined || item.sessionId === String(sessionId)));
  return runtime?.detach() || false;
}
